import { useEffect } from 'react';
import { create } from 'zustand';

/**
 * REV-939: display names for breadcrumb segments that the URL cannot supply.
 *
 * A team's crumb read its title-cased slug and a tracked place's read either
 * its slug or, for a UUID, "Details". The breadcrumb has no way to know the
 * name, and fetching it per crumb would duplicate a request the page on screen
 * has already made. So the page that loads the entity publishes its name for
 * its own path, and the breadcrumb reads it from here.
 *
 * Keyed by the exact path the crumb links to (`/orgs/acme/teams/platform`), so
 * a label can only ever name the segment it was published for. Not persisted:
 * a label lives exactly as long as the page that published it is mounted.
 *
 * Kept out of `@store/index` on purpose. Page tests mock that module wholesale,
 * and a page publishing here must not need them to mock this too.
 */
interface BreadcrumbLabelState {
  labels: Record<string, string>;
  setLabel: (path: string, label: string) => void;
  clearLabel: (path: string) => void;
}

export const useBreadcrumbLabelStore = create<BreadcrumbLabelState>()((set) => ({
  labels: {},
  setLabel: (path, label) => set((state) => ({ labels: { ...state.labels, [path]: label } })),
  clearLabel: (path) =>
    set((state) => {
      const next = { ...state.labels };
      delete next[path];
      return { labels: next };
    }),
}));

/**
 * A label together with the crumb path it was loaded for.
 *
 * The page stamps the path when its load for that URL succeeds, not when it
 * renders: React Router keeps a page mounted when only a param changes, so on
 * `/teams/a` → `/teams/b` the page still holds team A while B loads — or for
 * good, if B's load fails. The stamp is what tells the two apart.
 */
export interface LoadedBreadcrumbLabel {
  path: string;
  label: string;
}

/**
 * Publish a name as the breadcrumb for `path` while the calling component is
 * mounted — but only when `loaded` was loaded for that same path.
 *
 * `path` is the crumb the page is on now, from the URL. `loaded` is the name
 * the page has, stamped with the path it was fetched for. When they differ the
 * page is showing a stale entity (a param change is still loading, failed, or
 * an older request resolved late) and nothing is published, so the crumb
 * falls back to its URL-derived label rather than naming the wrong entity.
 */
export function useBreadcrumbLabel(
  path: string | undefined,
  loaded: LoadedBreadcrumbLabel | null | undefined
): void {
  const label = loaded && loaded.path === path ? loaded.label : undefined;
  useEffect(() => {
    if (!path || !label) return undefined;
    const { setLabel, clearLabel } = useBreadcrumbLabelStore.getState();
    setLabel(path, label);
    return () => clearLabel(path);
  }, [path, label]);
}
